import dayjs from 'dayjs';

/**
 * Check if a new event conflicts with existing events
 * @param {Object} newEvent - {startTime, endTime}
 * @param {Array} existingEvents - Array of existing events
 * @returns {Object} {hasConflict: boolean, conflicts: Array}
 */
export const checkEventConflicts = (newEvent, existingEvents) => {
    const conflicts = [];
    const newStart = dayjs(newEvent.startTime);
    const newEnd = dayjs(newEvent.endTime);

    existingEvents.forEach(event => {
        const eventStart = dayjs(event.startTime);
        const eventEnd = dayjs(event.endTime);

        // Check for overlap
        if (newStart.isBefore(eventEnd) && newEnd.isAfter(eventStart)) {
            conflicts.push({
                event,
                overlapType: getOverlapType(newStart, newEnd, eventStart, eventEnd),
            });
        }
    });

    return {
        hasConflict: conflicts.length > 0,
        conflicts,
    };
};

/**
 * Determine the type of overlap between two events
 * @param {dayjs.Dayjs} start1
 * @param {dayjs.Dayjs} end1
 * @param {dayjs.Dayjs} start2
 * @param {dayjs.Dayjs} end2
 * @returns {string} Type of overlap
 */
const getOverlapType = (start1, end1, start2, end2) => {
    if (start1.isSame(start2) && end1.isSame(end2)) {
        return 'exact';
    }
    if (start1.isBefore(start2) && end1.isAfter(end2)) {
        return 'contains';
    }
    if (start1.isAfter(start2) && end1.isBefore(end2)) {
        return 'contained';
    }
    if (start1.isBefore(start2) && end1.isAfter(start2) && end1.isBefore(end2)) {
        return 'overlaps_start';
    }
    if (start1.isAfter(start2) && start1.isBefore(end2) && end1.isAfter(end2)) {
        return 'overlaps_end';
    }
    return 'partial';
};

/**
 * Find available time slots around conflicts
 * @param {Object} proposedEvent - {startTime, endTime, duration}
 * @param {Array} conflicts - Array of conflicting events
 * @param {Object} dayBounds - {start, end} working hours
 * @returns {Array} Suggested alternative slots
 */
export const suggestAlternativeSlots = (proposedEvent, conflicts, dayBounds) => {
    const suggestions = [];
    const duration = dayjs(proposedEvent.endTime).diff(dayjs(proposedEvent.startTime), 'minute');

    // Sort conflicts by start time
    const sortedConflicts = conflicts
        .map(c => c.event)
        .sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)));

    // Check before first conflict
    const firstConflict = sortedConflicts[0];
    const beforeFirst = dayjs(firstConflict.startTime).subtract(duration, 'minute');

    if (beforeFirst.isAfter(dayBounds.start) || beforeFirst.isSame(dayBounds.start)) {
        suggestions.push({
            start: beforeFirst,
            end: dayjs(firstConflict.startTime),
            position: 'before',
        });
    }

    // Check after last conflict
    const lastConflict = sortedConflicts[sortedConflicts.length - 1];
    const afterLast = dayjs(lastConflict.endTime);
    const afterLastEnd = afterLast.add(duration, 'minute');

    if (afterLastEnd.isBefore(dayBounds.end) || afterLastEnd.isSame(dayBounds.end)) {
        suggestions.push({
            start: afterLast,
            end: afterLastEnd,
            position: 'after',
        });
    }

    // Check gaps between conflicts
    for (let i = 0; i < sortedConflicts.length - 1; i++) {
        const gapStart = dayjs(sortedConflicts[i].endTime);
        const gapEnd = dayjs(sortedConflicts[i + 1].startTime);
        const gapDuration = gapEnd.diff(gapStart, 'minute');

        if (gapDuration >= duration) {
            suggestions.push({
                start: gapStart,
                end: gapStart.add(duration, 'minute'),
                position: 'between',
                gapIndex: i,
            });
        }
    }

    return suggestions;
};

/**
 * Check if moving an event would create conflicts
 * @param {Object} event - Event to move
 * @param {dayjs.Dayjs} newStartTime
 * @param {Array} allEvents - All events (excluding the one being moved)
 * @returns {Object} Conflict check result
 */
export const checkMoveConflicts = (event, newStartTime, allEvents) => {
    const duration = dayjs(event.endTime).diff(dayjs(event.startTime), 'minute');
    const newEndTime = newStartTime.add(duration, 'minute');

    // Filter out the event being moved
    const otherEvents = allEvents.filter(e => e.id !== event.id);

    return checkEventConflicts(
        { startTime: newStartTime, endTime: newEndTime },
        otherEvents
    );
};

/**
 * Check if resizing an event would create conflicts
 * @param {Object} event - Event to resize
 * @param {dayjs.Dayjs} newStartTime - New start time (if resizing from top)
 * @param {dayjs.Dayjs} newEndTime - New end time (if resizing from bottom)
 * @param {Array} allEvents - All events
 * @returns {Object} Conflict check result
 */
export const checkResizeConflicts = (event, newStartTime, newEndTime, allEvents) => {
    const otherEvents = allEvents.filter(e => e.id !== event.id);

    return checkEventConflicts(
        {
            startTime: newStartTime || event.startTime,
            endTime: newEndTime || event.endTime,
        },
        otherEvents
    );
};

/**
 * Validate event timing constraints
 * @param {dayjs.Dayjs} startTime
 * @param {dayjs.Dayjs} endTime
 * @param {Object} constraints - {minDuration, maxDuration, workingHours}
 * @returns {Object} {isValid: boolean, errors: Array}
 */
export const validateEventTiming = (startTime, endTime, constraints = {}) => {
    const errors = [];
    const duration = endTime.diff(startTime, 'minute');

    // Check minimum duration
    if (constraints.minDuration && duration < constraints.minDuration) {
        errors.push(`Minimum duration is ${constraints.minDuration} minutes`);
    }

    // Check maximum duration
    if (constraints.maxDuration && duration > constraints.maxDuration) {
        errors.push(`Maximum duration is ${constraints.maxDuration} minutes`);
    }

    // Check if within working hours
    if (constraints.workingHours) {
        const { start: dayStart, end: dayEnd } = constraints.workingHours;

        if (startTime.isBefore(dayStart)) {
            errors.push(`Cannot start before ${dayStart.format('HH:mm')}`);
        }

        if (endTime.isAfter(dayEnd)) {
            errors.push(`Cannot end after ${dayEnd.format('HH:mm')}`);
        }
    }

    // Check if end is after start
    if (!endTime.isAfter(startTime)) {
        errors.push('End time must be after start time');
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
};

/**
 * Find the next available slot for a given duration
 * @param {dayjs.Dayjs} preferredTime
 * @param {number} durationMinutes
 * @param {Array} existingEvents
 * @param {Object} dayBounds
 * @returns {Object|null} Next available slot or null
 */
export const findNextAvailableSlot = (preferredTime, durationMinutes, existingEvents, dayBounds) => {
    let currentTime = preferredTime.clone();
    const endOfDay = dayBounds.end;

    // Sort events by start time
    const sortedEvents = [...existingEvents].sort((a, b) =>
        dayjs(a.startTime).diff(dayjs(b.startTime))
    );

    while (currentTime.add(durationMinutes, 'minute').isBefore(endOfDay) ||
    currentTime.add(durationMinutes, 'minute').isSame(endOfDay)) {
        const proposedEnd = currentTime.add(durationMinutes, 'minute');

        // Check if this slot conflicts with any existing events
        const hasConflict = sortedEvents.some(event => {
            const eventStart = dayjs(event.startTime);
            const eventEnd = dayjs(event.endTime);

            return currentTime.isBefore(eventEnd) && proposedEnd.isAfter(eventStart);
        });

        if (!hasConflict) {
            return {
                start: currentTime,
                end: proposedEnd,
            };
        }

        // Move to the end of the conflicting event
        const conflictingEvent = sortedEvents.find(event => {
            const eventStart = dayjs(event.startTime);
            const eventEnd = dayjs(event.endTime);

            return currentTime.isBefore(eventEnd) && proposedEnd.isAfter(eventStart);
        });

        if (conflictingEvent) {
            currentTime = dayjs(conflictingEvent.endTime);
        } else {
            // This shouldn't happen, but move forward by 15 minutes as fallback
            currentTime = currentTime.add(15, 'minute');
        }
    }

    return null; // No available slot found
};